import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  platform,
  type PlatformEntitlement,
  type PlatformFeatureStatus,
  type PlatformPlan,
  type PlatformTenantDetail,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate, formatDateTime, formatDocument } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { GovernanceSection } from './GovernanceSection';
import { PlatformSettingsSection } from './PlatformSettingsSection';
import {
  fimDoDia,
  opcoesDePlano,
  paraCampoDeData,
  planos,
  rotuloDaAcao,
} from './labels';

/**
 * Empresa vista pela administracao da plataforma.
 *
 * As decisoes que moram aqui: o plano, a situacao, cada funcionalidade e as regras e
 * governanca (quem troca conta bancaria, quem muda credito do tomador, e se
 * documento pendente impede a emissao). Todas ficam na trilha da plataforma com o
 * estado anterior e o posterior, e a suspensao exige motivo, porque tira o acesso de
 * todas as pessoas da empresa de uma vez.
 */
export function PlatformTenantPage() {
  const { tenantId = '' } = useParams();

  const consulta = useQuery({
    queryKey: ['platform', 'tenant', tenantId],
    queryFn: ({ signal }) => platform.getTenant(tenantId, signal),
    enabled: tenantId !== '',
  });

  if (consulta.isPending) {
    return <p className="text-sm text-ink-500">Carregando a empresa.</p>;
  }
  if (consulta.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar a empresa">
        {consulta.error.message}
      </Alert>
    );
  }

  return <DetalheDaEmpresa empresa={consulta.data} />;
}

function DetalheDaEmpresa({ empresa }: { empresa: PlatformTenantDetail }) {
  const { session } = useAuth();
  const [mudandoPlano, setMudandoPlano] = useState(false);
  const [mudandoSituacao, setMudandoSituacao] = useState(false);
  const [funcionalidade, setFuncionalidade] = useState<PlatformEntitlement | null>(
    null,
  );

  const encerrada = empresa.status === 'closed';
  const daPropriaSessao = session?.tenant.id === empresa.id;

  const colunas: Column<PlatformEntitlement>[] = [
    {
      key: 'funcionalidade',
      header: 'Funcionalidade',
      render: (item) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{item.name}</p>
          <p className="truncate font-mono text-2xs text-ink-500">{item.key}</p>
        </div>
      ),
    },
    {
      key: 'plano',
      header: 'No plano',
      hideBelow: 'md',
      render: (item) =>
        item.in_plan ? (
          <Badge tone="brand">Incluida</Badge>
        ) : (
          <span className="text-ink-400">Fora do plano</span>
        ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (item) =>
        item.status ? (
          <StatusBadge kind="funcionalidade" value={item.status} />
        ) : (
          <span className="text-ink-400">Nao contratada</span>
        ),
    },
    {
      key: 'validade',
      header: 'Validade',
      hideBelow: 'sm',
      render: (item) =>
        item.expires_at ? (
          <span className="tabular-nums">{formatDate(item.expires_at)}</span>
        ) : (
          <span className="text-ink-400">Sem validade</span>
        ),
    },
    {
      key: 'vale',
      header: 'Vale agora',
      render: (item) =>
        item.usable ? (
          <Badge tone="success">Sim</Badge>
        ) : (
          <Badge tone="neutral">Nao</Badge>
        ),
    },
    {
      key: 'acao',
      header: 'Acao',
      align: 'right',
      render: (item) =>
        encerrada ? null : (
          <AcaoComIcone
            acao="editar"
            rotulo="Alterar"
            aria-label={`Alterar ${item.name}`}
            onClick={() => setFuncionalidade(item)}
          />
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/admin/empresas"
        className="text-xs font-medium text-brand-600 hover:underline"
      >
        Voltar para empresas
      </Link>

      <PageHeader
        title={empresa.name}
        description={`${empresa.legal_name}, CNPJ ${formatDocument(empresa.document)}`}
        meta={
          <>
            <Badge tone="brand">{planos[empresa.plan] ?? empresa.plan}</Badge>
            <StatusBadge kind="empresa" value={empresa.status} />
            <Badge tone="neutral">{empresa.slug}</Badge>
          </>
        }
      />

      {encerrada ? (
        <Alert tone="info" title="Empresa encerrada">
          Uma empresa encerrada nao e alterada pela administracao da plataforma.
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader
            title="Plano"
            description="Mudar o plano alinha as funcionalidades a ele."
          />
          <CardBody className="flex flex-col gap-3">
            <p className="text-sm text-ink-700">
              Plano atual:{' '}
              <span className="font-semibold">
                {planos[empresa.plan] ?? empresa.plan}
              </span>
            </p>
            {!encerrada ? (
              <Button variant="secondary" onClick={() => setMudandoPlano(true)}>
                Mudar plano
              </Button>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Situacao"
            description="Suspender interrompe o acesso de todas as pessoas da empresa."
          />
          <CardBody className="flex flex-col gap-3">
            <p className="text-sm text-ink-700">
              Situacao atual: <StatusBadge kind="empresa" value={empresa.status} />
            </p>
            {!encerrada ? (
              <Button
                variant={empresa.status === 'active' ? 'danger' : 'secondary'}
                onClick={() => setMudandoSituacao(true)}
              >
                {empresa.status === 'active' ? 'Suspender empresa' : 'Reativar empresa'}
              </Button>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Resumo" />
          <CardBody className="flex flex-col gap-1.5 text-sm text-ink-700">
            <p>
              Pessoas com acesso ativo:{' '}
              <span className="font-semibold">{empresa.member_count}</span>
            </p>
            <p>
              Funcionalidades valendo:{' '}
              <span className="font-semibold">{empresa.active_feature_count}</span>
            </p>
            <p className="text-2xs text-ink-500">
              Criada em {formatDateTime(empresa.created_at)}
            </p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Funcionalidades"
          description="Situacao e validade de cada funcionalidade do catalogo para esta empresa. A mudanca vale na requisicao seguinte de quem esta nela."
        />
        <DataTable
          columns={colunas}
          items={empresa.features}
          keyOf={(item) => item.key}
        />
      </Card>

      <GovernanceSection tenantId={empresa.id} encerrada={encerrada} />
      {/* P-PLAT: os campos de plataforma da empresa do OnPoint. */}
      <PlatformSettingsSection tenantId={empresa.id} encerrada={encerrada} />

      <TrilhaDaEmpresa tenantId={empresa.id} />

      {mudandoPlano ? (
        <MudarPlano empresa={empresa} onClose={() => setMudandoPlano(false)} />
      ) : null}
      {mudandoSituacao ? (
        <MudarSituacao
          empresa={empresa}
          daPropriaSessao={daPropriaSessao}
          onClose={() => setMudandoSituacao(false)}
        />
      ) : null}
      {funcionalidade ? (
        <MudarFuncionalidade
          tenantId={empresa.id}
          item={funcionalidade}
          onClose={() => setFuncionalidade(null)}
        />
      ) : null}
    </div>
  );
}

function useAtualizarEmpresa(tenantId: string, onDone: () => void, mensagem: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return function aoConcluir(atualizada: PlatformTenantDetail) {
    queryClient.setQueryData(['platform', 'tenant', tenantId], atualizada);
    void queryClient.invalidateQueries({ queryKey: ['platform'] });
    toast.success(mensagem);
    onDone();
  };
}

function MudarPlano({
  empresa,
  onClose,
}: {
  empresa: PlatformTenantDetail;
  onClose: () => void;
}) {
  const [plano, setPlano] = useState<PlatformPlan>(empresa.plan);
  const [motivo, setMotivo] = useState('');
  const concluir = useAtualizarEmpresa(empresa.id, onClose, 'Plano alterado.');

  const salvar = useMutation({
    mutationFn: () =>
      platform.updateTenant(empresa.id, {
        plan: plano,
        ...(motivo.trim() ? { reason: motivo } : {}),
      }),
    onSuccess: concluir,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Mudar plano"
      description={empresa.name}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={salvar.isPending}
            disabled={plano === empresa.plan}
            onClick={() => salvar.mutate()}
          >
            Aplicar plano
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel mudar o plano">
            {salvar.error.message}
          </Alert>
        ) : null}
        <Alert tone="warning" title="O plano define a base">
          As funcionalidades do plano novo ficam ativas e sem validade. As que nao fazem
          parte dele e estavam valendo ficam suspensas. Nada e apagado.
        </Alert>
        <Select
          label="Plano"
          value={plano}
          options={opcoesDePlano}
          onChange={(evento) => setPlano(evento.target.value as PlatformPlan)}
        />
        <Textarea
          label="Motivo"
          value={motivo}
          hint="Opcional. Fica na trilha da plataforma."
          onChange={(evento) => setMotivo(evento.target.value)}
        />
      </div>
    </Modal>
  );
}

function MudarSituacao({
  empresa,
  daPropriaSessao,
  onClose,
}: {
  empresa: PlatformTenantDetail;
  daPropriaSessao: boolean;
  onClose: () => void;
}) {
  const suspender = empresa.status === 'active';
  const [motivo, setMotivo] = useState('');
  const concluir = useAtualizarEmpresa(
    empresa.id,
    onClose,
    suspender ? 'Empresa suspensa.' : 'Empresa reativada.',
  );

  const salvar = useMutation({
    mutationFn: () =>
      platform.updateTenant(empresa.id, {
        status: suspender ? 'suspended' : 'active',
        reason: motivo,
      }),
    onSuccess: concluir,
  });

  // O servidor exige pelo menos cinco caracteres. Liberar o botao antes disso so
  // trocaria o aviso na tela por uma recusa do servidor.
  const motivoValido = motivo.trim().length >= 5;

  return (
    <Modal
      open
      onClose={onClose}
      title={suspender ? 'Suspender empresa' : 'Reativar empresa'}
      description={empresa.name}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant={suspender ? 'danger' : 'primary'}
            loading={salvar.isPending}
            disabled={!motivoValido || (suspender && daPropriaSessao)}
            onClick={() => salvar.mutate()}
          >
            {suspender ? 'Suspender' : 'Reativar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel mudar a situacao">
            {salvar.error.message}
          </Alert>
        ) : null}
        {suspender && daPropriaSessao ? (
          <Alert tone="warning" title="Esta e a empresa da sua sessao">
            Suspende-la encerraria o seu proprio acesso a esta tela. Peca a outra pessoa
            que administra a plataforma.
          </Alert>
        ) : null}
        <p className="text-sm text-ink-600">
          {suspender
            ? 'Todas as pessoas da empresa perdem o acesso na requisicao seguinte: entrar, renovar a sessao e qualquer tela autenticada passam a ser recusados. Dados e trilha ficam preservados.'
            : 'As pessoas da empresa voltam a entrar com os mesmos perfis de antes.'}
        </p>
        <Textarea
          label="Motivo"
          required
          value={motivo}
          hint="Obrigatorio, com pelo menos cinco caracteres. Fica na trilha da plataforma."
          onChange={(evento) => setMotivo(evento.target.value)}
        />
      </div>
    </Modal>
  );
}

const opcoesDeSituacao: { value: PlatformFeatureStatus; label: string }[] = (
  ['active', 'trial', 'suspended', 'expired'] as const
).map((situacao) => ({
  value: situacao,
  label: statusLabel('funcionalidade', situacao),
}));

function MudarFuncionalidade({
  tenantId,
  item,
  onClose,
}: {
  tenantId: string;
  item: PlatformEntitlement;
  onClose: () => void;
}) {
  const [situacao, setSituacao] = useState<PlatformFeatureStatus>(
    item.status ?? 'active',
  );
  const [validade, setValidade] = useState(paraCampoDeData(item.expires_at));
  const concluir = useAtualizarEmpresa(tenantId, onClose, 'Funcionalidade atualizada.');

  const salvar = useMutation({
    mutationFn: () =>
      platform.setFeature(tenantId, item.key, {
        status: situacao,
        expires_at: validade ? fimDoDia(validade) : null,
      }),
    onSuccess: concluir,
  });

  const exigeData = situacao === 'trial' && validade === '';

  return (
    <Modal
      open
      onClose={onClose}
      title={item.name}
      description={item.key}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={salvar.isPending}
            disabled={exigeData}
            onClick={() => salvar.mutate()}
          >
            Salvar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <Select
          label="Situacao"
          value={situacao}
          options={opcoesDeSituacao}
          onChange={(evento) =>
            setSituacao(evento.target.value as PlatformFeatureStatus)
          }
        />
        <Field
          label="Valida ate"
          type="date"
          value={validade}
          hint={
            situacao === 'trial'
              ? 'Obrigatoria na avaliacao. Vale ate o fim do dia escolhido.'
              : 'Opcional. Sem data, a funcionalidade nao vence.'
          }
          onChange={(evento) => setValidade(evento.target.value)}
        />
      </div>
    </Modal>
  );
}

function TrilhaDaEmpresa({ tenantId }: { tenantId: string }) {
  const consulta = useQuery({
    queryKey: ['platform', 'audit', { tenantId }],
    queryFn: ({ signal }) =>
      platform.audit({ tenant_id: tenantId, only_changes: true, limit: 10 }, signal),
  });

  const linhas = consulta.data?.items ?? [];

  return (
    <Card>
      <CardHeader
        title="Ultimas alteracoes"
        description="Trilha da plataforma desta empresa, sem as consultas."
        action={
          <Link
            to="/admin/trilha"
            className="text-xs font-medium text-brand-600 hover:underline"
          >
            Trilha completa
          </Link>
        }
      />
      <CardBody>
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar a trilha">
            {consulta.error.message}
          </Alert>
        ) : linhas.length === 0 ? (
          <p className="text-sm text-ink-500">
            {consulta.isPending
              ? 'Carregando a trilha.'
              : 'Nenhuma alteracao registrada.'}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {linhas.map((linha) => (
              <li key={linha.id} className="flex flex-wrap gap-x-3 py-2 text-sm">
                <span className="tabular-nums text-ink-500">
                  {formatDateTime(linha.occurred_at)}
                </span>
                <span className="font-medium text-ink-900">
                  {rotuloDaAcao(linha.action)}
                </span>
                <span className="text-ink-600">{linha.actor_label}</span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
